<script setup lang="ts">
/**
 * Debounced text search box for use above a `DynamicTable`. `modelValue` is
 * the debounced query — the parent only sees a new value `debounceMs` after
 * the user stops typing, so it can be bound straight to a query key without
 * firing a request on every keystroke. Typed characters still appear
 * immediately via the internal `localValue`, which mirrors the input.
 */
import { onUnmounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    debounceMs?: number
  }>(),
  { placeholder: 'Search…', debounceMs: 300 },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const localValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (value) => {
    if (value !== localValue.value) localValue.value = value
  },
)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function onInput() {
  if (debounceTimer !== null) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    emit('update:modelValue', localValue.value)
  }, props.debounceMs)
}

onUnmounted(() => {
  if (debounceTimer !== null) clearTimeout(debounceTimer)
})
</script>

<template>
  <input
    v-model="localValue"
    type="text"
    class="table-search-input"
    :placeholder="placeholder"
    @input="onInput"
  />
</template>

<style scoped>
.table-search-input {
  width: 100%;
  max-width: 18rem;
  box-sizing: border-box;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
  padding: 0.4rem 0.7rem;
  outline: none;
  transition: border-color 0.12s;
}

.table-search-input:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}

.table-search-input::placeholder {
  color: var(--color-tufte-muted);
}
</style>
